import { CommonModule } from '@angular/common';
import {
    Component,
    HostBinding,
    ViewEncapsulation,
    inject,
} from '@angular/core';
import {
    MAT_DIALOG_DATA,
    MatDialogModule,
    MatDialogRef,
} from '@angular/material/dialog';
import type {
    ComparisonWidgetTypeConfig,
    DataSource,
    IndividualTimeFrame,
    TimeFrame,
} from '@clean-code/models';
import type { IDashboardWidgetDetailDto } from '@clean-code/shared/components/ui-dashboard';
import { ProgressBarComponent } from '@clean-code/shared/components/ui-progress-bar';
import { TimeSpanToggleComponent } from '@clean-code/shared/components/ui-time-span-toggle';
import { ComparisonChartComponent } from '@clean-code/time-series-identifier/ui-comparison-chart';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faTimes } from '@fortawesome/pro-solid-svg-icons';
import { TranslocoModule } from '@jsverse/transloco';
import { BehaviorSubject, map } from 'rxjs';

@Component({
  selector: 'comparison-chart-widget-dialog',
  templateUrl: './chart-dialog.component.html',
  styleUrls: ['./chart-dialog.component.scss'],
  encapsulation: ViewEncapsulation.None,
  imports: [
    CommonModule,
    MatDialogModule,
    FontAwesomeModule,
    TranslocoModule,
    ProgressBarComponent,
    ComparisonChartComponent,
    TimeSpanToggleComponent,
  ],
})
export class ChartDialogComponent {
  @HostBinding('class') class = 'chart-comparison-widget-dialog';
  data: {
    widget: IDashboardWidgetDetailDto;
    config: ComparisonWidgetTypeConfig;
  };

  public dialogRef = inject(MatDialogRef<ChartDialogComponent>);

  public faTimes = faTimes;
  public selectedTimeFrame$ = new BehaviorSubject<TimeFrame>('M_3');
  public isLoading$ = new BehaviorSubject<boolean>(false);
  public data$ = this.selectedTimeFrame$.pipe(
    map((timeFrame: TimeFrame) => {
      return {
        ...this.data,
        identifiers: this.data.config.dataSources.map(
          (x: DataSource) => x.settings,
        ),
        timeFrame: timeFrame,
        showInPercentage: this.data.config.settings.showInPercentage,
        showTable: this.data.config.settings.showTable,
      };
    }),
  );

  constructor() {
    const data: {
      widget: IDashboardWidgetDetailDto;
      config: ComparisonWidgetTypeConfig;
    } = inject(MAT_DIALOG_DATA);
    this.data = data;

    const timeFrame = data.config.settings.startDate
      ? ({
          start: new Date(data.config.settings.startDate),
          end: new Date(),
        } as IndividualTimeFrame)
      : data.config.settings.timeFrame;

    this.selectedTimeFrame$.next(timeFrame);
  }

  onNoClick(): void {
    this.dialogRef.close();
  }

  onClickClose(): void {
    this.dialogRef.close();
  }
}
